/* Renders a vendor profile from the hardcoded `VENDORS` data map.
   Used when the slug matches a curated/legacy vendor with rich content. */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkles, Check, Play } from "lucide-react";
import { PageShell } from "@/components/sections";
import VendorReviews from "@/components/VendorReviews";
import { Slideshow } from "./Slideshow";
import { MediaGrid, Lightbox } from "./MediaGrid";

export const HardcodedVendorProfile = ({
  vendor,
  slug,
  showcaseIndex,
  setShowcaseIndex,
  showcaseRef,
  showcaseImgRef,
}) => {
  const hasFullProfile =
    (vendor.gridItems && vendor.gridItems.length > 0) ||
    (vendor.gallery && vendor.gallery.length > 0) ||
    (vendor.bio && vendor.bio.length > 0) ||
    (vendor.services && vendor.services.length > 0) ||
    (vendor.stats && vendor.stats.length > 0);

  return (
    <PageShell testId={`vendor-profile-${slug}`}>
      {/* Hero / banner */}
      <section data-testid="vendor-profile-hero" className="relative w-full">
        <div className="relative h-[58vh] min-h-[420px] w-full overflow-hidden">
          <img
            src={vendor.image}
            alt={vendor.name}
            style={
              vendor.bannerPosition
                ? { objectPosition: vendor.bannerPosition }
                : undefined
            }
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"
          />
          <div className="relative z-10 h-full flex items-end">
            <div className="mx-auto w-full max-w-7xl px-6 md:px-12 pb-12 md:pb-16">
              <Link
                to={`/category/${vendor.category}`}
                data-testid="vendor-back-link"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold)] hover:text-white transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={1.6} />
                Back to {vendor.categoryTitle}
              </Link>
              <h1
                data-testid="vendor-name"
                className="mt-5 font-serif-display text-4xl sm:text-5xl md:text-6xl text-white font-medium leading-[1.05]"
              >
                {vendor.name}
              </h1>
              {vendor.tagline && (
                <p className="mt-3 max-w-xl text-[15px] md:text-base text-white/80 italic font-serif-display">
                  {vendor.tagline}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {hasFullProfile ? (
        <>
          {/* Instagram-style media grid (preferred) */}
          {vendor.gridItems && vendor.gridItems.length > 0 && (
            <section
              data-testid="vendor-media-grid"
              className="relative px-4 sm:px-6 md:px-12 pt-14 md:pt-20"
            >
              <div className="mx-auto max-w-6xl">
                <div className="text-center mb-10">
                  <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                    The Feed
                  </span>
                  <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
                    Recent Work
                  </h2>
                  <div className="mx-auto mt-5 h-px w-16 bg-[var(--ld-gold)]/60" />
                  <p className="mt-5 text-[14px] text-[var(--ld-ink-muted)]">
                    Tap any tile to open.
                  </p>
                </div>
                <MediaGrid items={vendor.gridItems} vendor_slug={vendor.slug} />
              </div>
            </section>
          )}

          {/* Legacy gallery slideshow (used when no gridItems) */}
          {!vendor.gridItems && vendor.gallery && vendor.gallery.length > 0 && (
            <section
              data-testid="vendor-gallery"
              className="relative px-6 md:px-12 pt-14 md:pt-20"
            >
              <div className="mx-auto max-w-6xl">
                <Slideshow
                  images={vendor.gallery}
                  alt={vendor.name}
                  orientation={vendor.galleryOrientation || "landscape"}
                  vendor_slug={vendor.slug}
                />
                <p className="mt-4 text-center text-[11px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                  Selected moments · {vendor.gallery.length} slides
                </p>
              </div>
            </section>
          )}

          {/* About / bio */}
          {vendor.bio && vendor.bio.length > 0 && (
            <section
              data-testid="vendor-about"
              className="relative px-6 md:px-12 py-16 md:py-24"
            >
              <div className="mx-auto max-w-5xl">
                <div className="text-center">
                  <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                    About
                  </span>
                  <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight">
                    Meet {vendor.name}
                  </h2>
                  <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
                </div>

                {vendor.portrait ? (
                  <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
                    {/* Portrait — left column on desktop */}
                    <div className="md:col-span-5 lg:col-span-5">
                      <div className="relative overflow-hidden bg-[var(--ld-ink)]">
                        <img
                          src={vendor.portrait}
                          alt={vendor.name}
                          loading="lazy"
                          data-testid="vendor-portrait"
                          className="block w-full h-auto aspect-[4/5] object-cover"
                        />
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 ring-1 ring-[var(--ld-gold)]/30"
                        />
                      </div>
                    </div>

                    {/* Bio paragraphs — right column on desktop */}
                    <div className="md:col-span-7 lg:col-span-7 space-y-5 text-[15.5px] leading-[1.85] text-[var(--ld-ink)]/85">
                      {vendor.bio.map((para, i) => (
                        <p key={`${i}-${para.slice(0, 24)}`}>{para}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-10 max-w-3xl mx-auto space-y-6 text-[15.5px] leading-[1.85] text-[var(--ld-ink)]/85">
                    {vendor.bio.map((para, i) => (
                      <p key={`${i}-${para.slice(0, 24)}`}>{para}</p>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Stats */}
          {vendor.stats && vendor.stats.length > 0 && (
            <section
              data-testid="vendor-stats"
              className="relative px-6 md:px-12 pb-4 md:pb-6"
            >
              <div className="mx-auto max-w-5xl">
                <ul
                  data-testid="vendor-stats-list"
                  className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-[var(--ld-border)] border border-[var(--ld-border)]"
                >
                  {vendor.stats.map((s) => (
                    <li
                      key={s.label}
                      className="px-8 py-10 sm:py-12 text-center"
                    >
                      <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)]">
                        {s.label}
                      </p>
                      <p className="mt-3 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-none">
                        {s.value}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Services */}
          {vendor.services && vendor.services.length > 0 && (
            <section
              data-testid="vendor-services"
              className="relative px-6 md:px-12 py-16 md:py-24 bg-white/40 border-y border-[var(--ld-border)]"
            >
              <div className="mx-auto max-w-5xl">
                <div className="text-center">
                  <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                    What&apos;s Included
                  </span>
                  <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight">
                    Services Provided
                  </h2>
                  <p className="mx-auto mt-4 max-w-xl text-[15px] text-[var(--ld-ink-muted)]">
                    Everything {vendor.name} brings to your event — from the
                    first planning call to the final song.
                  </p>
                  <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
                </div>

                <ul
                  data-testid="vendor-services-list"
                  className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4"
                >
                  {vendor.services.map((s) => (
                    <li
                      key={s}
                      className="flex items-start gap-3 text-[14.5px] leading-[1.6] text-[var(--ld-ink)]/90"
                    >
                      <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]">
                        <Check size={11} strokeWidth={2} />
                      </span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Showcase: banner + videos (after services) */}
          {vendor.showcase &&
            vendor.showcase.videos &&
            vendor.showcase.videos.length > 0 && (
              <section
                ref={showcaseRef}
                data-testid="vendor-showcase"
                className="relative w-full"
              >
                {/* Wide banner */}
                <div className="relative w-full h-[55vh] min-h-[380px] overflow-hidden">
                  <img
                    ref={showcaseImgRef}
                    src={vendor.showcase.banner}
                    alt={`${vendor.name} showcase`}
                    style={{
                      objectPosition:
                        vendor.showcase.bannerPosition || undefined,
                      transform: "translate3d(0, 0, 0) scale(1.14)",
                      willChange: "transform",
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-[var(--ld-ink)] via-black/55 to-black/30"
                  />
                  <div className="relative z-10 h-full flex items-end">
                    <div className="mx-auto w-full max-w-6xl px-6 md:px-12 pb-12 md:pb-16 text-center">
                      <span className="font-accent text-[11px] tracking-[0.3em] uppercase text-[var(--ld-gold)]">
                        Showcase
                      </span>
                      <h2
                        data-testid="showcase-heading"
                        className="mt-3 font-serif-display text-3xl sm:text-4xl md:text-5xl text-white font-medium leading-tight"
                      >
                        {vendor.showcase.heading || "See us in action"}
                      </h2>
                      {vendor.showcase.blurb && (
                        <p className="mx-auto mt-4 max-w-xl text-[15px] text-white/80">
                          {vendor.showcase.blurb}
                        </p>
                      )}
                      <div className="mx-auto mt-6 h-px w-16 bg-[var(--ld-gold)]/70" />
                    </div>
                  </div>
                </div>

                {/* Videos band */}
                <div className="bg-[var(--ld-ink)] px-4 sm:px-6 md:px-12 py-12 md:py-16">
                  <div className="mx-auto max-w-6xl">
                    <ul
                      data-testid="showcase-videos"
                      className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5"
                    >
                      {vendor.showcase.videos.map((v, i) => (
                        <li key={v.src}>
                          <button
                            type="button"
                            data-testid={`showcase-video-${i}`}
                            onClick={() => setShowcaseIndex(i)}
                            aria-label={`Play ${v.alt || "video"}`}
                            className="group relative block w-full overflow-hidden bg-black focus:outline-none focus:ring-2 focus:ring-[var(--ld-gold)] focus:ring-offset-2 focus:ring-offset-[var(--ld-ink)]"
                          >
                            <div className="aspect-video w-full">
                              <video
                                src={v.src}
                                poster={v.poster}
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                autoPlay
                                className="block w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                              />
                            </div>
                            <div
                              aria-hidden
                              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"
                            />
                            <div
                              aria-hidden
                              className="pointer-events-none absolute inset-0 ring-1 ring-transparent group-hover:ring-[var(--ld-gold)]/55 transition-colors duration-500"
                            />
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                              <span className="inline-flex h-14 w-14 items-center justify-center bg-black/40 backdrop-blur-sm border border-[var(--ld-gold)]/70 text-[var(--ld-gold)] transition-transform duration-500 group-hover:scale-110">
                                <Play
                                  size={20}
                                  strokeWidth={1.5}
                                  fill="currentColor"
                                />
                              </span>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {showcaseIndex !== null && (
                  <Lightbox
                    items={vendor.showcase.videos.map((v) => ({
                      type: "video",
                      src: v.src,
                      poster: v.poster,
                      alt: v.alt,
                    }))}
                    index={showcaseIndex}
                    onClose={() => setShowcaseIndex(null)}
                    onPrev={() =>
                      setShowcaseIndex(
                        (i) =>
                          (i - 1 + vendor.showcase.videos.length) %
                          vendor.showcase.videos.length
                      )
                    }
                    onNext={() =>
                      setShowcaseIndex(
                        (i) => (i + 1) % vendor.showcase.videos.length
                      )
                    }
                  />
                )}
              </section>
            )}

          {/* Reviews (works for both hardcoded and DB-vendor slugs) */}
          <VendorReviews slug={vendor.slug} />

          {/* CTA */}
          <section className="relative px-6 md:px-12 py-20 md:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <h3 className="font-serif-display text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
                Ready to book {vendor.name}?
              </h3>
              <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)]">
                Send us a quick enquiry and we&apos;ll connect you directly.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                <Link
                  to="/contact"
                  data-testid="vendor-cta-enquire"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:text-[#e9d59a] hover:bg-black transition-colors duration-300"
                >
                  Enquire about {vendor.name}
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </Link>
                <Link
                  to={`/category/${vendor.category}`}
                  data-testid="vendor-cta-back-category"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/80 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors duration-300"
                >
                  See more {vendor.categoryTitle}
                </Link>
              </div>
            </div>
          </section>
        </>
      ) : (
        /* Coming soon fallback */
        <section className="relative px-6 md:px-12 py-20 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center justify-center h-12 w-12 bg-[var(--ld-ink)] text-[var(--ld-gold)]">
              <Sparkles size={18} strokeWidth={1.5} />
            </span>
            <p className="mt-8 font-accent text-[11px] tracking-[0.3em] uppercase text-[var(--ld-gold-deep)]">
              Profile in progress
            </p>
            <h2
              data-testid="vendor-coming-soon"
              className="mt-4 font-serif-display text-3xl sm:text-4xl md:text-5xl text-[var(--ld-ink)] font-medium leading-tight"
            >
              {vendor.name}&apos;s full profile is coming soon.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-[var(--ld-ink-muted)]">
              {vendor.tagline}
            </p>
            <p className="mt-3 text-[14px] text-[var(--ld-ink-muted)]">
              Bio, gallery, packages and direct contact details will be live
              here shortly. In the meantime, reach the Luxé Directory team for
              an introduction.
            </p>
            <div className="mx-auto mt-10 h-px w-20 bg-[var(--ld-gold)]/60" />
            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link
                to="/contact"
                data-testid="vendor-cta-enquire"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-ink)] hover:text-[#e9d59a] hover:bg-black transition-colors duration-300"
              >
                Enquire about {vendor.name}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
              <Link
                to={`/category/${vendor.category}`}
                data-testid="vendor-cta-back-category"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase bg-transparent text-[var(--ld-ink)] border border-[var(--ld-ink)]/80 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors duration-300"
              >
                See more {vendor.categoryTitle}
              </Link>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
};
